import axios from "axios";
import type { ArbitrageOpportunity } from "../types/opportunity";
import {
  buildTraderApiBasePath,
  getTraderRouteVersion,
} from "../utils/traderRouting";

/**
 * Strategy type matching backend Rust enum
 */
export type StrategyType =
  | "Cross"
  | "HedgedCross"
  | "StandbyHedgedCross"
  | "AdaptiveHedgedCross"
  | "StandbyHedged";

/**
 * Price level structure matching Rust PriceLevel
 */
export interface PriceLevel {
  price: number;
  volume: number;
}

/**
 * Hedge configuration matching Rust HedgeConfig
 */
export interface HedgeConfig {
  leverage: number; // Leverage multiplier (1-50)
  open?: PriceLevel[]; // Futures price levels for opening short
  close?: PriceLevel[]; // Futures price levels for closing short
  rebalancing?: {
    price_surge_threshold?: number;
    liquidation_proximity_threshold?: number;
    entry_funding_rate_threshold?: number | null;
    entry_funding_interval_hours?: number;
    spot_futures_gap_magnitude_ratio?: number;
    low_funding_rate_threshold?: number;
    funding_time_window_seconds?: number;
  };
}

/**
 * Profit tier structure matching Rust ProfitTier
 */
export interface ProfitTier {
  profit_percentage: number; // e.g., 0.05 for 5%
  volume_percentage_to_sell: number; // e.g., 0.30 for 30%, 0 for remaining
}

/**
 * Minimal opportunity data for backend (matching Rust ArbitrageOpportunity struct)
 * Only includes fields that backend actually uses
 */
export interface BackendOpportunity {
  buy_exchange: number; // Exchange enum value
  sell_exchange: number; // Exchange enum value
  base: string; // Base currency (e.g., "BTC", "ETH")
  buy_quote: string; // Quote currency for buy ("USDT", "KRW", or "BTC")
  sell_quote: string; // Quote currency for sell ("USDT", "KRW", or "BTC")
  buy_levels: PriceLevel[]; // Buy orderbook levels
  sell_levels: PriceLevel[]; // Sell orderbook levels
  quote_conversion_rate: number; // Conversion rate between quote currencies
}

/**
 * Cross strategy configuration
 */
export interface CrossConfig {
  opportunity: BackendOpportunity;
  network: null;
  usdt_budget: number;
}

/**
 * HedgedCross strategy configuration
 * NOTE: When profit_tiers is empty = immediate execution (HedgedCross)
 *       When profit_tiers is non-empty = adaptive execution (AdaptiveHedgedCross)
 */
export interface HedgedCrossConfig {
  opportunity: BackendOpportunity;
  hedge: HedgeConfig;
  profit_tiers?: ProfitTier[]; // Optional: empty/undefined = HedgedCross, non-empty = AdaptiveHedgedCross
  network: null;
  usdt_budget: number;
}

/**
 * StandbyHedgedCross strategy configuration
 */
export interface StandbyHedgedCrossConfig {
  opportunity: BackendOpportunity;
  hedge: HedgeConfig;
  profit_tiers: ProfitTier[];
  network: null;
  usdt_budget: number;
  buyopen_mode?:
    | { type: "Limit" }
    | { type: "Market"; max_notional_usdt: number };
  replay_enabled?: boolean;
  replay_delay_seconds?: number;
}

/**
 * StandbyHedged strategy configuration (no transfer - same exchange)
 */
export interface StandbyHedgedConfig {
  opportunity: BackendOpportunity;
  hedge: HedgeConfig;
  profit_tiers: ProfitTier[];
  usdt_budget: number;
  buyopen_mode?:
    | { type: "Limit" }
    | { type: "Market"; max_notional_usdt: number };
  replay_enabled?: boolean;
  replay_delay_seconds?: number;
}

/**
 * Strategy execution request matching backend Rust format
 * NOTE: AdaptiveHedgedCross is sent as HedgedCross with profit_tiers populated
 */
export interface StrategyExecutionRequest {
  config: {
    Cross?: CrossConfig;
    HedgedCross?: HedgedCrossConfig; // Can be HedgedCross or AdaptiveHedgedCross based on profit_tiers
    StandbyHedgedCross?: StandbyHedgedCrossConfig;
    StandbyHedged?: StandbyHedgedConfig;
  };
}

/**
 * Strategy execution response from backend
 */
export interface StrategyExecutionResponse {
  strategy_id: string; // UUID of strategy execution
  message: string; // Status message
}

/**
 * Calculate actual tradable volume considering ALL fees.
 * For hedge strategies, accounts for: buy fee + withdrawal fee + futures taker fee
 * For non-hedge strategies, accounts for: buy fee only
 *
 * @returns Final volume that can be used for trading (after all fee deductions)
 */
function calculateTradableVolume(
  opportunity: ArbitrageOpportunity,
  budgetUSD: number,
  isHedgeStrategy: boolean,
): {
  finalVolume: number;
  volumeBeforeFees: number;
  volumeAfterBuyFee: number;
  budgetInBuyQuote: number;
} {
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  console.log("📊 VOLUME CALCULATION START");
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

  const buyFee = opportunity.buy_fee || 0;

  // Additional fees for hedge strategies
  // TODO: Make these configurable in settings
  const WITHDRAWAL_FEE_RATE = 0.0001; // 0.01% - typical withdrawal fee
  const FUTURES_TAKER_FEE = 0.0005; // 0.05% - typical futures taker fee

  console.log("💰 Input Parameters:", {
    budgetUSD,
    isHedgeStrategy,
    buyFee: `${(buyFee * 100).toFixed(3)}%`,
    withdrawalFeeRate: isHedgeStrategy
      ? `${(WITHDRAWAL_FEE_RATE * 100).toFixed(3)}%`
      : "N/A",
    futuresTakerFee: isHedgeStrategy
      ? `${(FUTURES_TAKER_FEE * 100).toFixed(3)}%`
      : "N/A",
  });

  // Convert budget from USD to buy quote currency
  let budgetInBuyQuote = budgetUSD;
  if (opportunity.buy_quote === "K") {
    budgetInBuyQuote = budgetUSD * 1535;
    console.log(
      `💱 Currency Conversion: ${budgetUSD} USD → ${budgetInBuyQuote.toFixed(2)} KRW`,
    );
  } else if (opportunity.buy_quote === "B") {
    budgetInBuyQuote = budgetUSD / 100000;
    console.log(
      `💱 Currency Conversion: ${budgetUSD} USD → ${budgetInBuyQuote.toFixed(8)} BTC`,
    );
  } else {
    console.log(`💱 No conversion needed (USDT ≈ USD): ${budgetUSD}`);
  }

  // Consume buy levels with available budget
  let remainingBudget = budgetInBuyQuote;
  let volumeBeforeFees = 0;

  console.log("\n🛒 Buy Level Consumption:");
  console.log("  Starting budget:", budgetInBuyQuote);

  for (const level of opportunity.buy_levels) {
    if (remainingBudget <= 0) break;

    const costAtLevel = level.price * level.volume;
    if (costAtLevel <= remainingBudget) {
      volumeBeforeFees += level.volume;
      remainingBudget -= costAtLevel;
      console.log(
        `  ✓ Full level - price: ${level.price}, volume: ${level.volume}, cost: ${costAtLevel.toFixed(4)}, remaining: ${remainingBudget.toFixed(4)}`,
      );
    } else {
      const volumeAtLevel = remainingBudget / level.price;
      volumeBeforeFees += volumeAtLevel;
      console.log(
        `  ⚡ Partial level - price: ${level.price}, volume: ${volumeAtLevel.toFixed(6)}, cost: ${remainingBudget.toFixed(4)}`,
      );
      remainingBudget = 0;
    }
  }

  console.log(
    `  📦 Total volume before fees: ${volumeBeforeFees.toFixed(6)}\n`,
  );

  // Apply spot buy fee
  const volumeAfterBuyFee = volumeBeforeFees * (1 - buyFee);
  const buyFeeLoss = volumeBeforeFees - volumeAfterBuyFee;
  console.log(`💳 After spot buy fee (${(buyFee * 100).toFixed(3)}%):`, {
    before: volumeBeforeFees.toFixed(6),
    fee: buyFeeLoss.toFixed(6),
    after: volumeAfterBuyFee.toFixed(6),
  });

  // For hedge strategies, apply additional fees
  let finalVolume = volumeAfterBuyFee;

  if (isHedgeStrategy) {
    console.log("\n🔒 Hedge Strategy - Additional Fee Deductions:");

    // Withdrawal fee (transfer from spot to futures)
    const afterWithdrawal = finalVolume * (1 - WITHDRAWAL_FEE_RATE);
    const withdrawalLoss = finalVolume - afterWithdrawal;
    console.log(
      `  📤 Withdrawal fee (${(WITHDRAWAL_FEE_RATE * 100).toFixed(3)}%):`,
      {
        before: finalVolume.toFixed(6),
        fee: withdrawalLoss.toFixed(6),
        after: afterWithdrawal.toFixed(6),
      },
    );
    finalVolume = afterWithdrawal;

    // Futures taker fee (opening hedge position)
    const afterFuturesFee = finalVolume * (1 - FUTURES_TAKER_FEE);
    const futuresFeeLoss = finalVolume - afterFuturesFee;
    console.log(
      `  🎯 Futures taker fee (${(FUTURES_TAKER_FEE * 100).toFixed(3)}%):`,
      {
        before: finalVolume.toFixed(6),
        fee: futuresFeeLoss.toFixed(6),
        after: afterFuturesFee.toFixed(6),
      },
    );
    finalVolume = afterFuturesFee;

    const totalHedgeFees = withdrawalLoss + futuresFeeLoss;
    console.log(
      `  ⚠️  Total hedge fees: ${totalHedgeFees.toFixed(6)} (${((totalHedgeFees / volumeAfterBuyFee) * 100).toFixed(3)}%)`,
    );
  }

  console.log("\n✅ FINAL VOLUME CALCULATION:");
  console.log(`  Volume before any fees: ${volumeBeforeFees.toFixed(6)}`);
  console.log(`  Volume after buy fee:   ${volumeAfterBuyFee.toFixed(6)}`);
  console.log(`  Final tradable volume:  ${finalVolume.toFixed(6)}`);
  console.log(
    `  Total fee impact:       ${((1 - finalVolume / volumeBeforeFees) * 100).toFixed(3)}%`,
  );
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");

  return {
    finalVolume,
    volumeBeforeFees,
    volumeAfterBuyFee,
    budgetInBuyQuote,
  };
}

/**
 * Create backend opportunity with FULL orderbook levels.
 * Backend will handle trimming based on budget.
 */
function createBackendOpportunity(
  opportunity: ArbitrageOpportunity,
): BackendOpportunity {
  console.log("\n📦 Creating Backend Opportunity:");
  console.log(
    `  Buy levels:  ${opportunity.buy_levels.length} levels (full orderbook)`,
  );
  console.log(
    `  Sell levels: ${opportunity.sell_levels.length} levels (full orderbook)`,
  );

  return {
    buy_exchange: opportunity.buy_exchange,
    sell_exchange: opportunity.sell_exchange,
    base: opportunity.base,
    buy_quote: opportunity.buy_quote,
    sell_quote: opportunity.sell_quote,
    buy_levels: opportunity.buy_levels, // Send full orderbook
    sell_levels: opportunity.sell_levels, // Send full orderbook
    quote_conversion_rate: opportunity.quote_conversion_rate,
  };
}

/**
 * Options for strategy execution
 */
export interface ExecuteStrategyOptions {
  opportunity: ArbitrageOpportunity;
  strategyType: StrategyType;
  budget: number;
  leverage?: number; // For hedge strategies
  hedgeRebalancing?: HedgeConfig["rebalancing"]; // Optional standby hedge rebalancing settings
  profitTiers?: ProfitTier[]; // For StandbyHedgedCross
  adaptiveProfitPercentage?: number; // For AdaptiveHedgedCross (decimal format: 0.05 = 5%)
  futuresOpenLevels?: PriceLevel[]; // Futures price levels for opening short position
  buyOpenMode?: StandbyHedgedConfig["buyopen_mode"];
  replayEnabled?: boolean;
  replayDelaySeconds?: number;
  previewOnly?: boolean; // If true, return request JSON without executing
  traderName: string; // The trader name to execute strategy for
}

/**
 * Trim futures open levels based on calculated volume.
 * For hedge strategies, we need to match the futures position size to the spot volume.
 */
function trimFuturesLevels(
  futuresLevels: PriceLevel[] | undefined,
  targetVolume: number,
): PriceLevel[] | undefined {
  if (!futuresLevels || futuresLevels.length === 0) {
    return undefined;
  }

  console.log("\n🎯 Trimming Futures Levels:");
  console.log("  Target volume:", targetVolume.toFixed(6));

  let remainingVolume = targetVolume;
  const trimmedLevels: PriceLevel[] = [];

  for (const level of futuresLevels) {
    if (remainingVolume <= 0) break;

    const volumeAtLevel = Math.min(remainingVolume, level.volume);
    trimmedLevels.push({ price: level.price, volume: volumeAtLevel });
    remainingVolume -= volumeAtLevel;
    console.log(`  ✓ ${level.price} @ ${volumeAtLevel.toFixed(6)}`);
  }

  console.log(
    `  📊 Used ${trimmedLevels.length} futures levels for ${(targetVolume - remainingVolume).toFixed(6)} volume`,
  );

  return trimmedLevels;
}

/**
 * Get the API base URL based on environment and trader name
 */
function getApiBaseUrl(traderName: string): string {
  const isProduction = window.location.hostname !== "localhost";
  const apiBasePath = buildTraderApiBasePath(
    traderName,
    getTraderRouteVersion(window.location.pathname),
  );
  if (isProduction) {
    return `${window.location.protocol}//${window.location.host}${apiBasePath}`;
  } else {
    // Development: use same port as main API
    return `http://${window.location.hostname}:8765${apiBasePath}`;
  }
}

/**
 * Execute a strategy with a pre-built backend payload.
 * Use this when you've already constructed the request in the correct backend format.
 *
 * @param request - Pre-built strategy execution request
 * @param traderName - The trader name to execute strategy for
 * @returns Promise with strategy execution response
 * @throws Error if execution fails
 */
export async function executeStrategyWithPayload(
  request: StrategyExecutionRequest,
  traderName: string,
): Promise<StrategyExecutionResponse> {
  const apiUrl = `${getApiBaseUrl(traderName)}/strategies`;
  console.log(`📤 Sending pre-built request to ${apiUrl}:`);
  console.log(JSON.stringify(request, null, 2));

  try {
    const response = await axios.post<StrategyExecutionResponse>(
      apiUrl,
      request,
      { timeout: 10000 },
    );
    console.log("✅ Strategy execution initiated successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Strategy execution failed:", error);
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 503) {
        throw new Error("Strategy execution service not yet available");
      }
      const errorMsg =
        error.response?.data?.message || "Failed to execute strategy";
      console.error("Error details:", error.response?.data);
      throw new Error(errorMsg);
    }
    throw error;
  }
}

/**
 * Execute a strategy for an arbitrage opportunity.
 *
 * @param options - Strategy execution options
 * @returns Promise with strategy execution response
 * @throws Error if execution fails
 */
export async function executeStrategy(
  options: ExecuteStrategyOptions,
): Promise<StrategyExecutionResponse | { requestJson: string }> {
  const {
    opportunity,
    strategyType,
    budget,
    leverage = 2,
    hedgeRebalancing,
    profitTiers = [],
    adaptiveProfitPercentage,
    futuresOpenLevels,
    buyOpenMode,
    replayEnabled,
    replayDelaySeconds,
    previewOnly = false,
    traderName,
  } = options;

  console.log("\n");
  console.log("🚀 ═══════════════════════════════════════════════════════════");
  console.log("🚀 STRATEGY EXECUTION REQUEST");
  console.log("🚀 ═══════════════════════════════════════════════════════════");
  console.log(`Strategy Type: ${strategyType}`);
  console.log(`Budget: $${budget} USD`);
  console.log(`Leverage: ${leverage}x`);
  console.log(`Base: ${opportunity.base}`);
  console.log(
    `Buy: ${opportunity.buy_quote} @ Exchange ${opportunity.buy_exchange}`,
  );
  console.log(
    `Sell: ${opportunity.sell_quote} @ Exchange ${opportunity.sell_exchange}`,
  );
  console.log("═══════════════════════════════════════════════════════════\n");

  // Calculate final tradable volume ONCE (considering all fees for hedge strategies)
  const isHedgeStrategy = strategyType !== "Cross";
  const volumeCalculation = calculateTradableVolume(
    opportunity,
    budget,
    isHedgeStrategy,
  );

  // Create backend opportunity with FULL orderbook levels (backend handles trimming)
  const backendOpportunity = createBackendOpportunity(opportunity);

  // For hedge strategies, trim futures levels to match the final volume
  const trimmedFuturesLevels = isHedgeStrategy
    ? trimFuturesLevels(futuresOpenLevels, volumeCalculation.finalVolume)
    : undefined;

  let request: StrategyExecutionRequest;

  if (strategyType === "Cross") {
    console.log("\n📝 Building Cross strategy request");
    console.log(
      "  Sending full orderbook levels (backend handles budget trimming)",
    );
    request = {
      config: {
        Cross: {
          opportunity: backendOpportunity, // Full orderbook levels
          network: null,
          usdt_budget: budget, // Backend uses this to trim levels
        },
      },
    };
  } else if (
    strategyType === "HedgedCross" ||
    strategyType === "AdaptiveHedgedCross"
  ) {
    // Both HedgedCross and AdaptiveHedgedCross use the same config
    // AdaptiveHedgedCross is HedgedCross with profit_tiers populated

    // Use provided profit percentage or calculate from opportunity
    let profitPercentage = 0.05; // Default fallback
    if (strategyType === "AdaptiveHedgedCross") {
      if (adaptiveProfitPercentage !== undefined) {
        // Use user-provided profit percentage
        profitPercentage = adaptiveProfitPercentage;
        console.log(
          "💰 Using provided profit percentage for AdaptiveHedgedCross:",
          {
            profitPercentage: `${(profitPercentage * 100).toFixed(2)}%`,
            decimal: profitPercentage,
          },
        );
      } else {
        // Calculate from opportunity prices and fees (fallback)
        const buyPrice = opportunity.buy_price || 0;
        const sellPrice = opportunity.sell_price || 0;
        const buyFee = opportunity.buy_fee || 0;
        const sellFee = opportunity.sell_fee || 0;

        if (buyPrice > 0 && sellPrice > 0) {
          // Net profit percentage: (sell_net - buy_cost) / buy_cost
          const buyCost = buyPrice * (1 + buyFee);
          const sellNet = sellPrice * (1 - sellFee);
          profitPercentage = (sellNet - buyCost) / buyCost;

          console.log(
            "💰 Calculated profit percentage for AdaptiveHedgedCross:",
            {
              buyPrice,
              sellPrice,
              buyFee: `${(buyFee * 100).toFixed(3)}%`,
              sellFee: `${(sellFee * 100).toFixed(3)}%`,
              buyCost,
              sellNet,
              profitPercentage: `${(profitPercentage * 100).toFixed(2)}%`,
            },
          );
        }
      }
    }

    const adaptiveProfitTier: ProfitTier | undefined =
      strategyType === "AdaptiveHedgedCross"
        ? {
            profit_percentage: profitPercentage,
            volume_percentage_to_sell: 1.0, // Always 100% for adaptive
          }
        : undefined;

    console.log(`\n📝 Building ${strategyType} request:`, {
      leverage,
      profitTier: adaptiveProfitTier,
      futuresLevelsProvided: !!futuresOpenLevels,
      originalFuturesLevels: futuresOpenLevels?.length || 0,
      trimmedFuturesLevels: trimmedFuturesLevels?.length || 0,
      finalVolume: volumeCalculation.finalVolume.toFixed(6),
    });
    console.log(
      "  Spot levels: Full orderbook (backend handles budget trimming)",
    );
    console.log("  Futures levels: Trimmed to match final volume");

    request = {
      config: {
        HedgedCross: {
          opportunity: backendOpportunity, // Full orderbook levels
          hedge: {
            leverage,
            open: trimmedFuturesLevels, // Trimmed to match final volume after fees
          },
          profit_tiers: adaptiveProfitTier ? [adaptiveProfitTier] : [], // Empty for HedgedCross, single tier for Adaptive
          network: null,
          usdt_budget: budget, // Backend uses this to trim spot levels
        },
      },
    };
  } else if (strategyType === "StandbyHedgedCross") {
    const standbyHedge: HedgeConfig = {
      leverage,
      open: trimmedFuturesLevels,
      ...(hedgeRebalancing ? { rebalancing: hedgeRebalancing } : {}),
    };

    console.log("\n📝 Building StandbyHedgedCross request:", {
      leverage,
      profitTiers: profitTiers.length,
      futuresLevelsProvided: !!futuresOpenLevels,
      originalFuturesLevels: futuresOpenLevels?.length || 0,
      trimmedFuturesLevels: trimmedFuturesLevels?.length || 0,
      finalVolume: volumeCalculation.finalVolume.toFixed(6),
      buyOpenMode,
      replayEnabled,
      replayDelaySeconds,
      hedgeRebalancing,
    });
    console.log(
      "  Spot levels: Full orderbook (backend handles budget trimming)",
    );
    console.log("  Futures levels: Trimmed to match final volume");

    request = {
      config: {
        StandbyHedgedCross: {
          opportunity: backendOpportunity, // Full orderbook levels
          hedge: standbyHedge,
          profit_tiers: profitTiers,
          network: null,
          usdt_budget: budget, // Backend uses this to trim spot levels
          ...(buyOpenMode ? { buyopen_mode: buyOpenMode } : {}),
          ...(replayEnabled !== undefined
            ? { replay_enabled: replayEnabled }
            : {}),
          ...(replayDelaySeconds !== undefined
            ? { replay_delay_seconds: replayDelaySeconds }
            : {}),
        },
      },
    };
  } else if (strategyType === "StandbyHedged") {
    const standbyHedge: HedgeConfig = {
      leverage,
      open: trimmedFuturesLevels,
      ...(hedgeRebalancing ? { rebalancing: hedgeRebalancing } : {}),
    };

    console.log(
      "\n📝 Building StandbyHedged request (same exchange - no transfer):",
      {
        leverage,
        profitTiers: profitTiers.length,
        futuresLevelsProvided: !!futuresOpenLevels,
        originalFuturesLevels: futuresOpenLevels?.length || 0,
        trimmedFuturesLevels: trimmedFuturesLevels?.length || 0,
        finalVolume: volumeCalculation.finalVolume.toFixed(6),
        buyOpenMode,
        replayEnabled,
        replayDelaySeconds,
        hedgeRebalancing,
      },
    );
    console.log(
      "  Spot levels: Full orderbook (backend handles budget trimming)",
    );
    console.log("  Futures levels: Trimmed to match final volume");
    console.log("  ⚠️  No transfer - same exchange strategy");

    request = {
      config: {
        StandbyHedged: {
          opportunity: backendOpportunity, // Full orderbook levels
          hedge: standbyHedge,
          profit_tiers: profitTiers,
          usdt_budget: budget, // Backend uses this to trim spot levels
          ...(buyOpenMode ? { buyopen_mode: buyOpenMode } : {}),
          ...(replayEnabled !== undefined
            ? { replay_enabled: replayEnabled }
            : {}),
          ...(replayDelaySeconds !== undefined
            ? { replay_delay_seconds: replayDelaySeconds }
            : {}),
        },
      },
    };
  } else {
    throw new Error(`Unknown strategy type: ${strategyType}`);
  }

  // Final summary
  console.log("\n");
  console.log("📋 ═══════════════════════════════════════════════════════════");
  console.log("📋 REQUEST SUMMARY");
  console.log("📋 ═══════════════════════════════════════════════════════════");
  console.log(`Strategy: ${strategyType}`);
  console.log(
    `Budget: $${budget} USD → ${volumeCalculation.budgetInBuyQuote.toFixed(6)} ${opportunity.buy_quote}`,
  );
  console.log(`\nVolume Calculation (for futures trimming):`);
  console.log(
    `  Before fees:     ${volumeCalculation.volumeBeforeFees.toFixed(6)} ${opportunity.base}`,
  );
  console.log(
    `  After buy fee:   ${volumeCalculation.volumeAfterBuyFee.toFixed(6)} ${opportunity.base}`,
  );
  console.log(
    `  Final tradable:  ${volumeCalculation.finalVolume.toFixed(6)} ${opportunity.base}`,
  );
  console.log(
    `  Fee impact:      ${((1 - volumeCalculation.finalVolume / volumeCalculation.volumeBeforeFees) * 100).toFixed(3)}%`,
  );
  console.log(`\nOrderbook Levels Sent to Backend:`);
  console.log(
    `  Spot buy:        ${backendOpportunity.buy_levels.length} levels (FULL - backend trims by budget)`,
  );
  console.log(
    `  Spot sell:       ${backendOpportunity.sell_levels.length} levels (FULL - backend trims by budget)`,
  );
  if (trimmedFuturesLevels) {
    console.log(
      `  Futures open:    ${trimmedFuturesLevels.length} levels (TRIMMED to ${volumeCalculation.finalVolume.toFixed(6)} ${opportunity.base})`,
    );
  }
  if (isHedgeStrategy) {
    console.log(`\nHedge Config:`);
    console.log(`  Leverage:        ${leverage}x`);
    console.log(
      `  Position size:   ${volumeCalculation.finalVolume.toFixed(6)} ${opportunity.base} (after all fees)`,
    );
  }
  console.log("═══════════════════════════════════════════════════════════\n");

  // If preview only, return the JSON string
  if (previewOnly) {
    console.log("👁️  Preview mode - not executing");
    return { requestJson: JSON.stringify(request, null, 2) };
  }

  // Log full request for debugging
  const apiUrl = `${getApiBaseUrl(traderName)}/strategies`;
  console.log(`📤 Sending request to ${apiUrl}:`);
  console.log(JSON.stringify(request, null, 2));
  console.log("\n");

  try {
    const response = await axios.post<StrategyExecutionResponse>(
      apiUrl,
      request,
      { timeout: 10000 },
    );
    console.log("✅ Strategy execution initiated successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Strategy execution failed:", error);
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 503) {
        throw new Error("Strategy execution service not yet available");
      }
      const errorMsg =
        error.response?.data?.message || "Failed to execute strategy";
      console.error("Error details:", error.response?.data);
      throw new Error(errorMsg);
    }
    throw error;
  }
}

/**
 * Withdrawal limit information from exchange
 */
export interface WithdrawalLimit {
  currency: string;
  minimum: string;
  onetime: string;
  daily: string | null;
  remaining_daily_fiat: string;
  fiat_currency: string;
  fixed: number | null;
  can_withdraw: boolean;
  withdraw_delayed_fiat: string | null;
}

/**
 * Account balance information
 */
export interface AccountBalance {
  balance: string;
  locked: string;
  avg_buy_price: string;
  unit_currency: string;
}

/**
 * Withdrawal chance response from backend
 */
export interface WithdrawalChanceResponse {
  status: string;
  exchange: string;
  currency: string;
  network: string;
  withdraw_limit: WithdrawalLimit;
  account: AccountBalance;
}

/**
 * Get withdrawal chance (daily limit info) for a specific currency/network
 * Used to check if daily withdrawal limit would cap the strategy budget
 *
 * @param traderName - Name of the trader
 * @param exchange - Exchange name (upbit, bithumb)
 * @param currency - Currency code (e.g., BTC, ETH)
 * @param network - Network name (e.g., BTC, TRC20, ERC20)
 * @returns Withdrawal chance information including daily limits
 */
export async function getWithdrawalChance(
  traderName: string,
  exchange: string,
  currency: string,
  network: string,
): Promise<WithdrawalChanceResponse> {
  const apiUrl = `${getApiBaseUrl(traderName)}/withdrawal_chance/${exchange}/${currency}/${network}`;
  console.log(`📤 Fetching withdrawal chance from ${apiUrl}`);

  try {
    const response = await axios.get<WithdrawalChanceResponse>(apiUrl, {
      timeout: 10000,
    });
    console.log("✅ Withdrawal chance retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to get withdrawal chance:", error);
    if (axios.isAxiosError(error)) {
      const errorMsg =
        error.response?.data?.message || "Failed to get withdrawal chance";
      throw new Error(errorMsg);
    }
    throw error;
  }
}

/**
 * Register strategy response from backend
 */
export interface RegisterStrategyResponse {
  status: string;
  strategy_id: string;
  action: "Resumed" | "AddedToCompleted";
  current_state: string;
  message: string;
}

/**
 * Register an existing strategy from snapshot file.
 * The snapshot must exist at: data/{trader_name}/strategies/{first_two_chars}/{strategy_id}.json
 *
 * @param traderName - Name of the trader
 * @param strategyId - UUID of the strategy to register
 * @returns Register result with action taken
 */
export async function registerStrategy(
  traderName: string,
  strategyId: string,
): Promise<RegisterStrategyResponse> {
  const apiUrl = `${getApiBaseUrl(traderName)}/strategies/register`;
  console.log(`📤 Registering strategy ${strategyId} at ${apiUrl}`);

  try {
    const response = await axios.post<RegisterStrategyResponse>(
      apiUrl,
      { strategy_id: strategyId },
      { timeout: 10000 },
    );
    console.log("✅ Strategy registered successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to register strategy:", error);
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const errorData = error.response?.data;

      // Handle specific error codes from backend
      if (status === 400) {
        throw new Error(errorData?.message || "Invalid strategy ID format");
      } else if (status === 404) {
        throw new Error(errorData?.message || "Snapshot file not found");
      } else if (status === 409) {
        throw new Error(errorData?.message || "Strategy is already running");
      }

      throw new Error(errorData?.message || "Failed to register strategy");
    }
    throw error;
  }
}
